import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "../components/layout/PageShell";
import {
    getCrmProjectDetail,
    generateLogoOptions,
    saveProjectLogo,
    getBrandingKit
} from "../services/crmService";
import toast from "react-hot-toast";

import "./project.css";

function getErrorMessage(error, fallback = "Something went wrong.") {
    const apiError = error?.response?.data;

    if (typeof apiError === "string") return apiError;
    if (apiError?.message) return apiError.message;
    if (apiError?.title) return apiError.title;
    if (error?.message) return error.message;

    return fallback;
}

export default function ProjectBrandingPage() {
    const { projectId } = useParams();

    const [project, setProject] = useState(null);
    const [logos, setLogos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [brandKit, setBrandKit] = useState(null);
    const [kitLoading, setKitLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadProject = useCallback(async () => {
        try {
            const data = await getCrmProjectDetail(projectId);
            setProject(data);
        } catch (error) {
            console.error(error);
            toast.error(getErrorMessage(error, "Failed to load project."));
        }
    }, [projectId]);

    useEffect(() => {
        loadProject();
    }, [loadProject]);

    const handleGenerateLogos = async () => {
        if (!project) return;

        try {
            setLoading(true);
            setBrandKit(null);
            setSelected(null);

            const res = await generateLogoOptions(projectId, {
                businessName: project.businessName,
                industry: project.industry,
                subIndustry: project.subIndustry
            });

            setLogos(res.options || []);
            toast.success("Design options generated.");
        } catch (error) {
            console.error(error);
            toast.error(getErrorMessage(error, "Failed to generate logos."));
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = async (logo) => {
        setSelected(logo);

        try {
            setKitLoading(true);
            const res = await getBrandingKit(projectId, logo.key);
            setBrandKit(res.kit);
        } catch (error) {
            console.error(error);
            toast.error(getErrorMessage(error, "Failed to load branding kit."));
        } finally {
            setKitLoading(false);
        }
    };

    const handleSave = async () => {
        if (!selected || !brandKit) {
            toast.error("Select a logo and branding kit first.");
            return;
        }

        try {
            setSaving(true);

            const result = await saveProjectLogo(projectId, {
                svg: selected.svg,
                style: selected.style,
                palette: selected.palette,
                brandingKit: brandKit
            });

            if (result) {
                toast.success("Complete brand identity saved.");
            } else {
                toast.error("Failed to save brand identity.");
            }
        } catch (error) {
            console.error(error);
            toast.error(getErrorMessage(error, "Failed to save logo."));
        } finally {
            setSaving(false);
        }
    };

    if (!project) {
        return (
            <PageShell title="Branding">
                <div className="project-loading-card">Loading branding workspace...</div>
            </PageShell>
        );
    }

    return (
        <PageShell title={`${project.businessName} Branding`}>
            <div className="branding-shell polished">
                <section className="module-hero-premium">
                    <div>
                        <span className="project-kicker">Visual Identity Engine</span>
                        <h1>Brand Generator</h1>
                        <p>
                            Generate logo options and a matching business identity kit for
                            {project.businessName ? ` ${project.businessName}` : " this project"}.
                        </p>

                        <div className="module-hero-meta">
                            <span>{project.industry || "Industry"}</span>
                            <span>{project.subIndustry || "Sub industry"}</span>
                            <span>{logos.length} logo options</span>
                        </div>
                    </div>

                    <div className="module-hero-actions">
                        <Link className="project-action-btn secondary" to={`/crm/projects/${projectId}`}>
                            Back to Project
                        </Link>

                        <button
                            className="project-action-btn primary"
                            onClick={handleGenerateLogos}
                            disabled={loading}
                        >
                            {loading ? "Generating..." : "Generate Design Options"}
                        </button>
                    </div>
                </section>

                <section className="project-panel">
                    <div className="project-panel-header">
                        <div>
                            <span className="project-kicker">Logo Options</span>
                            <h2>Select Brand Direction</h2>
                            <p>
                                Pick one option to generate a full branding kit, including supporting
                                visual assets and color palette.
                            </p>
                        </div>
                    </div>

                    {logos.length === 0 ? (
                        <div className="project-empty">
                            No logo options generated yet. Click “Generate Design Options” to start.
                        </div>
                    ) : (
                        <div className="logo-grid polished">
                            {logos.map((logo) => (
                                <button
                                    type="button"
                                    key={logo.key}
                                    className={`logo-card polished ${selected?.key === logo.key ? "active" : ""}`}
                                    onClick={() => handleSelect(logo)}
                                >
                                    <div className="logo-preview" dangerouslySetInnerHTML={{ __html: logo.svg }} />

                                    <div className="logo-meta">
                                        <strong>{logo.name}</strong>
                                        <span>{logo.style}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </section>

                {selected && (
                    <section className="project-panel brand-kit-section polished">
                        <div className="project-panel-header">
                            <div>
                                <span className="project-kicker">Brand Kit</span>
                                <h2>Your Full Branding Kit</h2>
                                <p>
                                    Matching assets generated from <strong>{selected.name}</strong>.
                                </p>
                            </div>
                        </div>

                        {kitLoading ? (
                            <div className="project-loading-card">Generating brand assets...</div>
                        ) : brandKit ? (
                            <>
                                <div className="kit-grid polished">
                                    {(brandKit.assets || []).map((asset, index) => (
                                        <article key={`${asset.name}-${index}`} className="asset-card polished">
                                            <div className="asset-label">{asset.name}</div>

                                            <div
                                                className="asset-preview"
                                                dangerouslySetInnerHTML={{ __html: asset.svg }}
                                            />

                                            <p className="asset-desc">{asset.description}</p>
                                        </article>
                                    ))}
                                </div>

                                <div className="branding-actions polished">
                                    <div className="color-pills polished">
                                        <div style={{ background: brandKit?.backgroundColor }}>
                                            <span>BG</span>
                                        </div>
                                        <div style={{ background: brandKit?.primaryColor }}>
                                            <span>Text</span>
                                        </div>
                                        <div style={{ background: brandKit?.accentColor }}>
                                            <span>Accent</span>
                                        </div>
                                    </div>

                                    <button
                                        className="project-action-btn primary"
                                        onClick={handleSave}
                                        disabled={saving}
                                    >
                                        {saving ? "Saving..." : "Save Brand Identity & Assets"}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="project-empty">
                                Select a logo option to generate matching brand assets.
                            </div>
                        )}
                    </section>
                )}
            </div>
        </PageShell>
    );
}